import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, EmptyState } from "./_ui";

const EMPTY = { full_name: "", role: "", photo_url: "", bio: "" };

export default function EquipeVitrinePanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/team").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.full_name || !form.role) return toast.error("Nom et fonction requis");
    setBusy(true);
    try {
      await api.post("/admin/team", form);
      toast.success("Membre ajouté à la vitrine");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Retirer ce membre de la vitrine ?")) return;
    await api.delete(`/admin/team/${id}`);
    toast.success("Retiré"); await load();
  };

  if (!items) return (<><PageHeader title="Équipe Vitrine" /><Spinner /></>);

  return (
    <div data-testid="admin-team">
      <PageHeader
        title="Équipe Vitrine"
        subtitle="Membres dirigeants affichés publiquement sur le site officiel A.F.B."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="team-create-btn">
              <Plus size={16} /> Ajouter un membre
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Nom complet" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="team-name" />
            <Input placeholder="Fonction (ex: Coordinateur National)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role" />
          </div>
          <Input placeholder="URL photo (facultatif)" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className="mt-3" />
          <Textarea placeholder="Biographie courte" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-3" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="team-save"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={UsersRound} title="Vitrine vide" hint="Ajoutez les dirigeants visibles sur le site public." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((m) => (
            <Card key={m.id} className="p-5 text-center" data-testid={`team-${m.id}`}>
              <div className="relative w-20 h-20 rounded-full mx-auto bg-gradient-to-br from-[#D4AF37]/30 to-[#0052CC]/30 grid place-items-center text-2xl font-display text-white border-2 border-[#D4AF37]/40 overflow-hidden">
                {m.photo_url ? (
                  <img src={m.photo_url} alt={m.full_name} className="w-full h-full object-cover" />
                ) : (
                  m.full_name.split(" ").slice(0, 2).map((s) => s[0]).join("").toUpperCase()
                )}
              </div>
              <h3 className="text-sm font-medium text-white mt-3">{m.full_name}</h3>
              <div className="text-xs text-[#D4AF37] mt-0.5">{m.role}</div>
              {m.bio && <p className="text-xs text-slate-400 mt-3 line-clamp-3">{m.bio}</p>}
              <button onClick={() => remove(m.id)} className="mt-4 text-xs text-slate-500 hover:text-[#CE1126] inline-flex items-center gap-1">
                <Trash2 size={11} /> Retirer
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
